import React, { useState, useContext, useEffect, useMemo } from "react";
import { db } from "services/firebase";
import { AuthContext } from "context/Auth";
import {
  Alert,
  Box,
  Chip,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import MonetizationOnRounded from "@mui/icons-material/MonetizationOnRounded";
import LocalHospitalRounded from "@mui/icons-material/LocalHospitalRounded";
import GroupRounded from "@mui/icons-material/GroupRounded";
import TrendingUpRounded from "@mui/icons-material/TrendingUpRounded";
import { calculateAggregatedCost } from "utils/costCalculator";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import Loading from "components/Loading";
import GraphKijabeUsage from "components/dashboard/admin/Graphs/GraphKijabeUsage";
import {
  CHART_HEIGHT,
  ControlBar,
  PanelHeader,
  PanelLoader,
  StatCard,
  UsagePanel,
  ZoneHeader,
  formatDayLabel,
} from "components/dashboard/usage/UsagePanels";
import {
  activeUserCount,
  encounterCostStats,
  fetchUsageRollups,
  projectMonthlySpend,
} from "services/usageRollupService";
import { cachedRead } from "services/usageCache";
import {
  NO_ORG_KEY,
  fetchOrganizationNames,
  fetchUserLabels,
} from "services/usageLabelService";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const RANGE_OPTIONS = [
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 60, label: "60 days" },
  { value: 90, label: "90 days" },
];

/**
 * Grouping dimensions, in the order an admin is likely to want them. Feature is first and
 * default: "what is ChartMind costing" is the question this page exists to answer, and it
 * used to be the one thing the page could not tell you.
 */
const GROUP_BY_OPTIONS = [
  { value: "byFeature", label: "Feature", heading: "Feature" },
  { value: "byModel", label: "AI model", heading: "Model" },
  { value: "byProvider", label: "Provider", heading: "Provider" },
  { value: "byOrg", label: "Organization", heading: "Organization" },
  { value: "byUser", label: "User", heading: "User" },
];

/** Feature keys are written by the backend; these are their display names. */
const FEATURE_LABELS = {
  chartmind: "ChartMind",
  carestart: "CareStart",
  "ask-me-anything": "Ask Me Anything",
  "peer-view": "Peer View",
  "document-indexing": "Document indexing",
  "health-probe": "Health probe",
  other: "Other",
};

/**
 * The per-encounter panel is ChartMind only, deliberately.
 *
 * An encounter needs an id shared by every call in it. ChartMind has one from the first step.
 * CareStart's session id only exists once a result has been written, so most of its calls
 * happen before there is anything to group them by — a "CareStart sessions" option would
 * either read empty or, worse, price a session from its last two calls. CareStart spend is
 * still on this page, under Feature.
 */
const ENCOUNTER_FEATURE = "chartmind";
const ENCOUNTER_LABEL = "ChartMind encounters";

/**
 * Money, at a precision that carries information.
 *
 * The shared formatCost goes to six decimals under a cent — thousandths of a cent. That is
 * meaningful for one API call, and every figure on this page is an aggregate: a day, a
 * feature, an encounter, a month. Nobody acts on the third decimal place of a cent.
 *
 * Four decimals below a dollar is not vanity, though: a median encounter at $0.0350 against a
 * p90 at $0.0620 is the spread this page exists to show, and rounding both to cents collapses
 * them to the same "$0.03". Above a dollar, cents are all anyone reads.
 *
 * A non-zero amount too small to render is shown as a bound rather than as $0.0000, which
 * would read as free.
 */
const formatMoney = (value) => {
  const n = Number(value || 0);
  if (n === 0) return "$0.00";
  if (n > 0 && n < 0.0001) return "<$0.0001";
  const decimals = n >= 1 ? 2 : 4;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: decimals,
  }).format(n);
};

const formatTokens = (n) => {
  const v = Number(n || 0);
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `${Math.round(v / 1e3)}K`;
  return v.toLocaleString();
};

/**
 * Rows shown in the breakdown table.
 *
 * Feature, provider and model have a handful of values each. Organization and user do not —
 * every clinician who ever ran a step is a row, so an unbounded table would grow with signups
 * and the name lookup with it. Ranked-and-capped is the useful view anyway: the question is
 * who the top spenders are, and the footer says what was left out.
 */
const MAX_BREAKDOWN_ROWS = 25;

/** The dimensions whose rollup keys are document ids rather than something readable. */
const ID_KEYED_GROUPINGS = ["byOrg", "byUser"];

const labelFor = (groupBy, key, resolved = {}) => {
  if (groupBy === "byFeature") return FEATURE_LABELS[key] || key;
  if (groupBy === "byOrg" && key === NO_ORG_KEY) return "No organization";
  if (groupBy === "byOrg" || groupBy === "byUser") return resolved[key] || key;
  return key;
};

/** Series colours from the theme, in a fixed order — no hardcoded palettes. */
const useSeriesColors = () => {
  const theme = useTheme();
  return useMemo(
    () => [
      theme.palette.primary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.info.main,
      theme.palette.error.main,
      theme.palette.secondary.main,
      theme.palette.text.disabled,
    ],
    [theme]
  );
};

/** Same columns for every dimension, so changing the grouping doesn't relayout the page. */
const BreakdownTable = ({
  rows,
  heading,
  groupBy,
  totalCost,
  labels,
  labelsPending,
  hiddenCount,
}) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{heading}</TableCell>
          <TableCell align="right">Requests</TableCell>
          <TableCell align="right">Input</TableCell>
          <TableCell align="right">Output</TableCell>
          <TableCell align="right">Cost</TableCell>
          <TableCell align="right">Share</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map(([key, data]) => (
          <TableRow key={key}>
            <TableCell>
              {/* A document id is noise, not a partial answer — skeleton the cell rather than
                  show one and swap it out. The row's numbers are already correct, so only the
                  name is held back. */}
              {labelsPending ? (
                <Skeleton variant="text" width="60%" sx={{ maxWidth: 220 }} />
              ) : (
                <Typography variant="body2">{labelFor(groupBy, key, labels)}</Typography>
              )}
            </TableCell>
            <TableCell align="right">{data.calls.toLocaleString()}</TableCell>
            <TableCell align="right">{formatTokens(data.inputTokens)}</TableCell>
            <TableCell align="right">{formatTokens(data.outputTokens)}</TableCell>
            <TableCell align="right">{formatMoney(data.cost)}</TableCell>
            <TableCell align="right">
              <Typography variant="body2" color="text.secondary">
                {totalCost > 0 ? `${((data.cost / totalCost) * 100).toFixed(1)}%` : "—"}
              </Typography>
            </TableCell>
          </TableRow>
        ))}
        {!rows.length && (
          <TableRow>
            <TableCell colSpan={6}>
              <Typography variant="body2" color="text.secondary">
                No activity in this period.
              </Typography>
            </TableCell>
          </TableRow>
        )}
        {hiddenCount > 0 && (
          <TableRow>
            <TableCell colSpan={6}>
              <Typography variant="body2" color="text.secondary">
                Showing the {rows.length} highest by cost. {hiddenCount.toLocaleString()} more
                not shown, but included in every total on this page.
              </Typography>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

const LegacySourceTable = ({ sources }) => {
  const sorted = Object.entries(sources).sort(([, a], [, b]) => b.cost - a.cost);
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Chatbot</TableCell>
            <TableCell align="right">Total tokens</TableCell>
            <TableCell align="right">Messages</TableCell>
            <TableCell align="right">Estimated cost</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map(([name, data]) => (
            <TableRow key={name}>
              <TableCell>{name}</TableCell>
              <TableCell align="right">{data.tokens.toLocaleString()}</TableCell>
              <TableCell align="right">{data.calls.toLocaleString()}</TableCell>
              <TableCell align="right">{formatMoney(data.cost)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const UsageMonitorPage = () => {
  const { userData } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const seriesColors = useSeriesColors();

  // One period control for the page. Grouping drives the chart and the table together, the
  // way the Anthropic and OpenAI usage pages do it — rather than four independent toggles
  // each showing a different slice of a different period.
  const [rangeDays, setRangeDays] = useState(30);
  const [groupBy, setGroupBy] = useState("byFeature");

  // Ids → names, for the two dimensions that store ids. Only the rows on screen are looked
  // up, so this stays a handful of reads however many clinicians the platform has. `signature`
  // records which view the names belong to; see labelsReady below.
  const [labels, setLabels] = useState({ signature: "", byId: {} });

  const [rollups, setRollups] = useState(null);
  const [rollupsLoading, setRollupsLoading] = useState(true);
  const [rollupsError, setRollupsError] = useState("");

  const [monthlyData, setMonthlyData] = useState(null);
  const [monthlyLoading, setMonthlyLoading] = useState(true);

  const availableMonths = useMemo(() => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i += 1) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`,
        label: date.toLocaleDateString("en-US", { year: "numeric", month: "long" }),
      });
    }
    return months;
  }, []);

  const [selectedMonth, setSelectedMonth] = useState(availableMonths[0].value);
  const selectedMonthLabel = useMemo(
    () => availableMonths.find((m) => m.value === selectedMonth)?.label || selectedMonth,
    [availableMonths, selectedMonth]
  );

  useEffect(() => {
    if (!userData?.admin) return undefined;

    let cancelled = false;
    setRollupsLoading(true);
    setRollupsError("");

    // Served from the module cache, so returning to this page costs no reads.
    fetchUsageRollups(rangeDays)
      .then((data) => {
        if (!cancelled) setRollups(data);
      })
      .catch((err) => {
        console.error("Failed to load usage rollups:", err);
        if (!cancelled) setRollupsError("Failed to load usage data.");
      })
      .finally(() => {
        if (!cancelled) setRollupsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userData, rangeDays]);

  useEffect(() => {
    if (!userData?.admin) return undefined;

    let cancelled = false;
    setMonthlyLoading(true);

    // Closed months never change, so this is cached per month like everything else.
    cachedRead(`usageMonthly:${selectedMonth}`, () =>
      db.collection("usage_monthly").doc(selectedMonth).get(),
    )
      .then((doc) => {
        if (cancelled) return;
        setMonthlyData(doc.exists ? calculateAggregatedCost(doc.data()) : null);
      })
      .catch((err) => {
        console.error("Failed to load legacy chatbot usage:", err);
        if (!cancelled) setMonthlyData(null);
      })
      .finally(() => {
        if (!cancelled) setMonthlyLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userData, selectedMonth]);

  const totals = rollups?.totals;
  const projection = useMemo(
    () => (rollups ? projectMonthlySpend(rollups.dailyRows) : null),
    [rollups]
  );
  // Prompt-testing sessions are excluded, which is the service default: they are our own
  // runs against the prompt editor, not encounters, and they skew every percentile here.
  const encounterStats = useMemo(
    () => (totals ? encounterCostStats(totals, { feature: ENCOUNTER_FEATURE }) : null),
    [totals]
  );
  const activeUsers = totals ? activeUserCount(totals) : 0;

  // Chart and table share the grouping. Series are the top keys by cost; the rest collapse
  // into "Other" so a stacked bar stays readable rather than growing a legend per user.
  const topKeys = useMemo(() => {
    if (!totals) return [];
    return Object.entries(totals[groupBy] || {})
      .sort(([, a], [, b]) => b.cost - a.cost)
      .slice(0, 6)
      .map(([key]) => key);
  }, [totals, groupBy]);

  const chartData = useMemo(() => {
    if (!rollups) return { labels: [], datasets: [] };

    return {
      labels: rollups.dailyRows.map((row) => formatDayLabel(row.dateKey)),
      datasets: topKeys.map((key, i) => ({
        label: labelFor(groupBy, key, labels.byId),
        data: rollups.dailyRows.map((row) => Number(row[groupBy]?.[key]?.cost ?? 0)),
        backgroundColor: seriesColors[i % seriesColors.length],
        stack: "usage",
      })),
    };
  }, [rollups, topKeys, groupBy, seriesColors, labels]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "top", labels: { color: theme.palette.text.secondary } },
        tooltip: {
          callbacks: {
            label: (ctx) => ` ${ctx.dataset.label}: ${formatMoney(ctx.parsed.y)}`,
          },
        },
      },
      // Axis text and grid from the theme: Chart.js defaults assume a light background.
      scales: {
        x: {
          stacked: true,
          ticks: { color: theme.palette.text.secondary },
          grid: { color: theme.palette.divider },
          border: { color: theme.palette.divider },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          ticks: { callback: (v) => formatMoney(v), color: theme.palette.text.secondary },
          grid: { color: theme.palette.divider },
          border: { color: theme.palette.divider },
        },
      },
    }),
    [theme]
  );

  const rankedRows = useMemo(() => {
    if (!totals) return [];
    return Object.entries(totals[groupBy] || {}).sort(([, a], [, b]) => b.cost - a.cost);
  }, [totals, groupBy]);

  const breakdownRows = useMemo(
    () => rankedRows.slice(0, MAX_BREAKDOWN_ROWS),
    [rankedRows]
  );
  const hiddenRowCount = rankedRows.length - breakdownRows.length;
  const needsLabels = ID_KEYED_GROUPINGS.includes(groupBy);

  // Which ids the current view needs names for. Doubles as the cache key for label state, so
  // "are these names the ones on screen" is derived rather than a separate loading flag —
  // a flag set inside an effect leaves one committed frame showing raw ids.
  const labelSignature = useMemo(() => {
    if (!needsLabels) return "";
    return `${groupBy}|${[...new Set([...topKeys, ...breakdownRows.map(([k]) => k)])]
      .sort()
      .join(",")}`;
  }, [needsLabels, groupBy, topKeys, breakdownRows]);

  const labelsReady = !needsLabels || labels.signature === labelSignature;

  useEffect(() => {
    if (!needsLabels) return undefined;

    let cancelled = false;
    const ids = [...new Set([...topKeys, ...breakdownRows.map(([key]) => key)])];
    const load = groupBy === "byOrg" ? fetchOrganizationNames : fetchUserLabels;

    // Resolved names are cached per id in the service, so switching grouping away and back
    // costs nothing and widening the period only looks up the handful of new ids.
    load(ids)
      .then((resolved) => {
        if (!cancelled) setLabels({ signature: labelSignature, byId: resolved });
      })
      .catch((err) => {
        // Marked ready with nothing resolved: the rows fall back to ids, which is degraded but
        // readable. Leaving it pending would skeleton the table forever.
        console.error("Failed to resolve usage labels:", err);
        if (!cancelled) setLabels({ signature: labelSignature, byId: {} });
      });

    return () => {
      cancelled = true;
    };
  }, [needsLabels, groupBy, topKeys, breakdownRows, labelSignature]);

  const groupMeta =
    GROUP_BY_OPTIONS.find((o) => o.value === groupBy) || GROUP_BY_OPTIONS[0];
  const encounterLabel = ENCOUNTER_LABEL;

  if (userData && !userData?.admin) navigate("/dashboard");
  // Nothing on the page has resolved yet, so this is the whole-view loading state rather than
  // a spinner in the corner of an empty page.
  if (!userData?.admin) return <Loading page />;

  return (
    <Box sx={{ mb: 4 }}>
      <DashboardPageHeader
        title="Usage Monitor"
        subtitle="AI spend and activity by feature, read from daily rollups."
      />

      {/* Controls: period and grouping. Everything below obeys both. */}
      <ControlBar>
        <ToggleButtonGroup
          value={rangeDays}
          exclusive
          onChange={(_, v) => v && setRangeDays(v)}
          size="small"
        >
          {RANGE_OPTIONS.map((o) => (
            <ToggleButton key={o.value} value={o.value} sx={{ px: 1.5 }}>
              {o.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel id="group-by-label">Group by</InputLabel>
          <Select
            labelId="group-by-label"
            label="Group by"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
          >
            {GROUP_BY_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </ControlBar>

      {rollupsError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {rollupsError}
        </Alert>
      )}

      {/* Unpriced means one thing only: no rate exists for that model, so spend is missing
          from the totals. A failed call is not unpriced — it has no tokens because it never
          ran, and $0 is the correct answer for it. Group by AI model to find the culprit. */}
      {totals?.unpricedCalls > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {totals.unpricedCalls.toLocaleString()} of {totals.calls.toLocaleString()} calls in
          this period ran on a model with no rate in the pricing table, so the totals below
          understate actual spend. Group by AI model to find it, then please add it to
          MODEL_METADATA in functions/src/nlp/query_llm.js.
        </Alert>
      )}

      {rollupsLoading ? (
        <PanelLoader />
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 4, alignItems: "stretch" }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total spend"
                value={formatMoney(totals?.cost || 0)}
                subtitle={
                  totals?.simulatedCost > 0
                    ? `${formatMoney(totals.simulatedCost)} of it prompt testing`
                    : `${(totals?.calls || 0).toLocaleString()} requests`
                }
                icon={<MonetizationOnRounded />}
                color="success"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Median per encounter"
                value={encounterStats ? formatMoney(encounterStats.median) : "—"}
                subtitle={
                  encounterStats
                    ? `${encounterStats.encounters.toLocaleString()} ${encounterLabel.toLowerCase()}`
                    : "No encounters in period"
                }
                icon={<LocalHospitalRounded />}
                color="primary"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Active users"
                value={activeUsers.toLocaleString()}
                subtitle={
                  activeUsers > 0
                    ? `${formatMoney((totals?.cost || 0) / activeUsers)} each`
                    : "Nobody used AI features"
                }
                icon={<GroupRounded />}
                color="info"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Projected this month"
                value={projection ? formatMoney(projection.projected) : "—"}
                subtitle={
                  projection
                    ? `${formatMoney(projection.spentSoFar)} through day ${projection.dayOfMonth}`
                    : "Widen the period to project"
                }
                icon={<TrendingUpRounded />}
                color="warning"
              />
            </Grid>
          </Grid>

          <UsagePanel sx={{ mb: 4 }}>
            <PanelHeader overline={`Daily spend by ${groupMeta.label.toLowerCase()}`} />
            {/* The legend is one label per series, so an unresolved grouping would put raw
                document ids in it. Hold the chart rather than render ids and swap them. The
                two branches keep the same height so the page does not jump. */}
            {labelsReady ? (
              <Box sx={{ height: CHART_HEIGHT }}>
                <Bar data={chartData} options={chartOptions} />
              </Box>
            ) : (
              <PanelLoader height={CHART_HEIGHT} />
            )}
          </UsagePanel>

          {/* The metric the question is actually about. A mean alone hides whether a long
              consultation costs ten times a short one, so show the spread. */}
          <UsagePanel sx={{ mb: 4 }}>
            <PanelHeader overline="Cost per encounter" title={encounterLabel} />

            {!encounterStats ? (
              <Alert severity="info">
                No {encounterLabel.toLowerCase()} recorded in this period.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {[
                  { label: "Median", value: encounterStats.median, note: "A typical encounter" },
                  { label: "Mean", value: encounterStats.mean, note: "Total ÷ encounters" },
                  { label: "90th percentile", value: encounterStats.p90, note: "9 in 10 cost less" },
                  { label: "Most expensive", value: encounterStats.max, note: "Worst case seen" },
                ].map((stat) => (
                  <Grid item xs={6} md={3} key={stat.label}>
                    <Typography variant="overline" color="text.secondary">
                      {stat.label}
                    </Typography>
                    <Typography variant="h6">{formatMoney(stat.value)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.note}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            )}
          </UsagePanel>

          <UsagePanel sx={{ mb: 6 }}>
            <PanelHeader overline={`Breakdown by ${groupMeta.label.toLowerCase()}`} />
            <BreakdownTable
              rows={breakdownRows}
              heading={groupMeta.heading}
              groupBy={groupBy}
              totalCost={totals?.cost || 0}
              labels={labels.byId}
              labelsPending={!labelsReady}
              hiddenCount={hiddenRowCount}
            />
          </UsagePanel>
        </>
      )}

      <Divider sx={{ mb: 4 }} />

      {/* Legacy: a separate subsystem with its own month-based aggregate and its own
          (frontend, OpenAI-only) cost estimate. Kept because it is the only record of
          chatbot usage, and labelled so its numbers are never read as platform totals. */}
      <Box sx={{ mb: 6 }}>
        <ZoneHeader
          overline="Legacy"
          title="Chatbot usage"
          note="Older chatbots only, aggregated per calendar month and costed by an earlier estimate. Not comparable with the figures above."
        />

        <UsagePanel>
          <PanelHeader
            title={selectedMonthLabel}
            action={
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  {availableMonths.map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            }
          />

          {monthlyLoading ? (
            <PanelLoader />
          ) : !monthlyData ? (
            <Alert severity="info">No chatbot usage recorded for {selectedMonthLabel}.</Alert>
          ) : (
            <>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
                <Chip label={`${formatMoney(monthlyData.totalCost)} estimated`} />
                <Chip label={`${monthlyData.userCount.toLocaleString()} users`} />
                <Chip label={`${monthlyData.totalCalls.toLocaleString()} messages`} />
              </Box>
              <LegacySourceTable sources={monthlyData.sources} />
            </>
          )}
        </UsagePanel>
      </Box>

      <Box>
        <ZoneHeader
          overline="Kijabe"
          title="Kijabe chatbots"
          note="Just the chatbots embedded on the Kijabe site, on their own."
        />
        <GraphKijabeUsage />
      </Box>
    </Box>
  );
};

export default UsageMonitorPage;
